import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import type { OutboundEmailPreference, OutreachPreferences } from '../pages/api/outreach-preferences';

export type { OutboundEmailPreference, OutreachPreferences };

const EMPTY: OutreachPreferences = { outboundChannel: null, outboundEmail: null, wantedChannels: [] };

async function fetchPreferences(): Promise<OutreachPreferences> {
  const res = await apiFetch(`${API_BASE}/api/outreach-preferences`, { credentials: 'include' });
  if (!res.ok) throw new Error(`outreach-preferences ${res.status}`);
  return (await res.json()) as OutreachPreferences;
}

async function putPreferences(patch: { outboundEmail?: OutboundEmailPreference | null; wantedChannels?: string[] }): Promise<OutreachPreferences> {
  const res = await apiFetch(`${API_BASE}/api/outreach-preferences`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
  if (!res.ok) throw new Error(`outreach-preferences ${res.status}`);
  return (await res.json()) as OutreachPreferences;
}

/** The account's outreach preferences (see the route), cached; `EMPTY` until read. */
export function useOutreachPreferences(): { prefs: OutreachPreferences; ready: boolean } {
  const query = useQuery({ queryKey: queryKeys.outreachPreferences, queryFn: fetchPreferences, staleTime: 60_000 });
  return { prefs: query.data ?? EMPTY, ready: query.status === 'success' };
}

/** Which address outreach email leaves from: a connected Gmail (`{ toolkit, connectedAccountId, label }`), or
 *  `null` for Symphony's own mailbox. Optimistic, rolled back on failure. */
export async function setOutboundEmail(pref: OutboundEmailPreference | null): Promise<void> {
  const prev = queryClient.getQueryData<OutreachPreferences>(queryKeys.outreachPreferences) ?? EMPTY;
  queryClient.setQueryData<OutreachPreferences>(queryKeys.outreachPreferences, { ...prev, outboundEmail: pref, outboundChannel: pref ? 'GMAIL_CONNECTOR' : 'EMAIL' });
  try {
    queryClient.setQueryData(queryKeys.outreachPreferences, await putPreferences({ outboundEmail: pref }));
  } catch (err) {
    queryClient.setQueryData(queryKeys.outreachPreferences, prev);
    throw err;
  }
}

/** Mark a coming-soon channel as wanted. Optimistic, rolled back on failure. */
export async function markChannelWanted(slug: string): Promise<void> {
  const prev = queryClient.getQueryData<OutreachPreferences>(queryKeys.outreachPreferences) ?? EMPTY;
  const wanted = Array.from(new Set([...prev.wantedChannels, slug]));
  queryClient.setQueryData<OutreachPreferences>(queryKeys.outreachPreferences, { ...prev, wantedChannels: wanted });
  try {
    queryClient.setQueryData(queryKeys.outreachPreferences, await putPreferences({ wantedChannels: wanted }));
  } catch (err) {
    queryClient.setQueryData(queryKeys.outreachPreferences, prev);
    throw err;
  }
}
